// One token: GET /tokens/:chain/:address kept current by the token channel (a fresh body as it
// changes, the price after each trade), its chart, its live trades, holders, top traders and pools.
import { useEffect, useState } from "react";
import {
  CHAINS,
  explorerAccount,
  explorerToken,
  explorerTx,
  get,
  LAUNCHPADS,
  tokenHref,
  useGet,
  useLive,
  useNow,
  walletHref,
  type ChainId,
  type Security,
  type TokenPage,
  type TokenRow,
  type Trade,
  type Window,
} from "./api";
import { Chart } from "./Chart";
import { age, amount, compact, pct, price, share, short, usd } from "./format";
import { plainPrice } from "./seo";
import { A, Change, Copy, Icon, Labels, Launchpad, Out, SocialLinks, TokenLogo } from "./ui";

export function Token({ chain, address }: { chain: ChainId; address: string }) {
  const { data: first, error } = useGet<TokenPage>(`/tokens/${chain}/${address}`);
  const [page, setPage] = useState<TokenPage | null>(null);
  useEffect(() => void (first && setPage(first)), [first]);
  useLive({ channel: "token", chain, token: address }, (m) => {
    if (m.type === "token") setPage(m.data as TokenPage);
    else if (m.type === "tick") setPage((p) => p && { ...p, ...(m.data as Partial<TokenPage>) });
  });

  const symbol = page?.token.symbol ?? short(address);
  useEffect(() => {
    // Until then the title the server wrote stands.
    if (page) document.title = `${symbol} ${plainPrice(page.price?.usd ?? null)} · price and chart on ${CHAINS[chain].name} – Aurelia Screener`;
  }, [symbol, page?.price?.usd, chain]);

  if (!page) {
    if (error) {
      return (
        <div className="empty-page">
          <h1>{error.status === 404 ? "Aurelia has not seen this token" : "This token could not be loaded"}</h1>
          <p className="muted">{error.status === 404 ? "It has not traded in a pool Aurelia indexes." : error.message}</p>
        </div>
      );
    }
    return <div className="page token-page loading-page" />;
  }

  const t = page.token;
  // A quote token nobody can name leaves out the pair and the figure in it, not the page.
  const quote = page.price?.quoteToken?.symbol;
  // The market cap's supply, where the token has a market cap: the chart and the trades price through it.
  const supply = page.marketCapUsd != null && t.circulatingSupply ? Number(t.circulatingSupply) : null;
  return (
    <div className="page token-page">
      <header className="card token-head">
        <div className="token-id">
          <TokenLogo src={t.logo} symbol={t.symbol} chain={chain} size={40} />
          <div className="token-title">
            <h1>
              {t.symbol ?? short(address)}
              {quote && <span className="muted pair"> / {quote}</span>}
            </h1>
            <span className="token-sub">
              <span className="muted token-name">{t.name}</span>
              <span className="addr">
                <span className="muted">{short(address)}</span>
                <Copy text={address} label="Copy the token address" />
                <Out href={explorerToken(chain, address)} className="icon-link" title={`Open in ${CHAINS[chain].explorerName}`}>
                  <Icon name="out" size={15} />
                </Out>
              </span>
              <SocialLinks socials={t.socials} />
            </span>
          </div>
        </div>
        <div className="head-stats">
          <HeadStat label="Market cap" value={usd(page.marketCapUsd)} />
          <HeadStat label="Price" value={price(page.price?.usd)} />
          {quote && <HeadStat label={`In ${quote}`} value={price(page.price?.quote, "")} />}
          <HeadStat label="Liquidity" value={usd(page.liquidityUsd)} />
          <HeadStat label="FDV" value={usd(page.fdvUsd)} />
        </div>
      </header>

      <div className="token-grid">
        <div className="token-main">
          <section className="card chart-card">
            <Chart chain={chain} token={address} pool={page.chartPool} supply={supply} />
          </section>
          <Tabs page={page} chain={chain} address={address} supply={supply} />
        </div>
        <aside className="token-side">
          <Windows windows={page.windows} />
          {page.bonding && <Bonding bonding={page.bonding} />}
          {t.security && <SecurityCard security={t.security} chain={chain} />}
          <Info page={page} chain={chain} />
        </aside>
      </div>
    </div>
  );
}

export function HeadStat({ label, value, tone }: { label: string; value: string; tone?: "up" | "down" }) {
  return (
    <div className="head-stat">
      <span className="stat-label">{label}</span>
      <span className={`head-stat-value num${tone ? ` ${tone}` : ""}`}>{value}</span>
    </div>
  );
}

function Windows({ windows }: { windows: TokenPage["windows"] }) {
  const [w, setW] = useState<keyof TokenPage["windows"]>("24h");
  const s: Window = windows[w];
  return (
    <section className="card side-card">
      <div className="windows" role="tablist" aria-label="Period">
        {(["5m", "1h", "6h", "24h"] as const).map((k) => (
          <button key={k} role="tab" aria-selected={w === k} className={w === k ? "window active" : "window"} onClick={() => setW(k)}>
            <span className="stat-label">{k.toUpperCase()}</span>
            <Change value={windows[k].priceChange} />
          </button>
        ))}
      </div>
      <Split label="Txns" total={compact(s.trades)} a={s.buys} b={s.sells} aLabel={`${compact(s.buys)} buys`} bLabel={`${compact(s.sells)} sells`} />
      <Split label="Volume" total={usd(s.volumeUsd)} a={s.buyVolumeUsd} b={s.sellVolumeUsd} aLabel={`${usd(s.buyVolumeUsd)} bought`} bLabel={`${usd(s.sellVolumeUsd)} sold`} />
      {s.buyers != null && s.sellers != null && (
        <Split label="Makers" total={compact(s.traders)} a={s.buyers} b={s.sellers} aLabel={`${compact(s.buyers)} buyers`} bLabel={`${compact(s.sellers)} sellers`} />
      )}
    </section>
  );
}

export function Split({ label, total, a, b, aLabel, bLabel }: { label: string; total: string; a: number; b: number; aLabel: string; bLabel: string }) {
  const f = a + b > 0 ? a / (a + b) : 0.5;
  return (
    <div className="split">
      <div className="split-head">
        <span className="stat-label">{label}</span>
        <span className="num">{total}</span>
      </div>
      <div className="split-bar" aria-hidden="true">
        <span className="up-bar" style={{ width: `${f * 100}%` }} />
        <span className="down-bar" />
      </div>
      <div className="split-foot muted num">
        <span>{aLabel}</span>
        <span>{bLabel}</span>
      </div>
    </div>
  );
}

function Bonding({ bonding }: { bonding: NonNullable<TokenPage["bonding"]> }) {
  return (
    <section className="card side-card">
      <div className="split-head">
        <span className="stat-label">Bonding curve</span>
        <span className="num">{bonding.complete ? "Graduated" : bonding.progress == null ? "–" : share(bonding.progress)}</span>
      </div>
      {!bonding.complete && bonding.progress != null && (
        <div className="progress" aria-hidden="true">
          <span style={{ width: `${bonding.progress * 100}%` }} />
        </div>
      )}
    </section>
  );
}

// ─── Security: each check as the API established it; a check it could not run says so ───

type Check = { label: string; value: string; tone: "ok" | "risk" | "unknown" };

function checks(s: Security, chain: ChainId): Check[] {
  const unknown = s.unknown ?? {};
  const out: Check[] = [];
  const add = (label: string, key: string, known: boolean, value: string, risky: boolean) =>
    out.push(unknown[key] || !known ? { label, value: "Unknown", tone: "unknown" } : { label, value, tone: risky ? "risk" : "ok" });

  if (chain === "solana") {
    const mintKnown = !unknown.mint;
    add("Mint authority", "mintAuthority", mintKnown, s.mintAuthority ? "Active" : "Revoked", !!s.mintAuthority);
    add("Freeze authority", "freezeAuthority", mintKnown, s.freezeAuthority ? "Active" : "Revoked", !!s.freezeAuthority);
    add("Metadata", "metadata", s.metadata?.mutable != null, s.metadata?.mutable ? "Mutable" : "Immutable", !!s.metadata?.mutable);
  } else {
    const c = s.contract;
    add("Owner", "contract", c?.renounced != null, c?.renounced ? "Renounced" : "Not renounced", !c?.renounced);
    add("Upgradeable", "contract", !!c, c?.proxy?.upgradeable ? "Yes" : "No", !!c?.proxy?.upgradeable);
    add("Buy tax", "taxes", s.taxes?.buy != null, share(s.taxes?.buy), (s.taxes?.buy ?? 0) >= 0.005);
    add("Sell tax", "taxes", s.taxes?.sell != null, share(s.taxes?.sell), (s.taxes?.sell ?? 0) >= 0.005);
  }
  const lp = s.lp;
  const held = lp ? (lp.burnedShare ?? 0) + (lp.lockedShare ?? 0) : null;
  add("LP burned or locked", "lp", lp?.burnedShare != null || lp?.lockedShare != null, share(held), (held ?? 0) < 0.5);
  add("Honeypot", "honeypot", s.honeypot?.likely != null, s.honeypot?.likely ? "Likely" : "No", !!s.honeypot?.likely);
  add("Top 10 holders", "top10Share", s.top10Share != null, share(s.top10Share), (s.top10Share ?? 0) > 0.5);
  return out;
}

function SecurityCard({ security, chain }: { security: Security; chain: ChainId }) {
  return (
    <section className="card side-card">
      <div className="split-head">
        <span className="stat-label">Security</span>
        <span className={security.flags.length ? "warn-text" : "muted"}>
          {security.flags.length ? `${security.flags.length} ${security.flags.length === 1 ? "flag" : "flags"}` : "No flags"}
        </span>
      </div>
      {!!security.flags.length && (
        <div className="flags">
          {security.flags.map((f) => (
            <span key={f} className="badge warn">
              {f.replace(/_/g, " ")}
            </span>
          ))}
        </div>
      )}
      <dl className="checks">
        {checks(security, chain).map((c) => (
          <div key={c.label} className="check">
            <dt className="muted">{c.label}</dt>
            <dd className={`check-${c.tone}`}>{c.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function Info({ page, chain }: { page: TokenPage; chain: ChainId }) {
  const t = page.token;
  const now = useNow(30_000);
  return (
    <section className="card side-card">
      <dl className="checks">
        {t.createdAt && (
          <div className="check">
            <dt className="muted">Created</dt>
            <dd>{age(t.createdAt, now)} ago</dd>
          </div>
        )}
        {page.creator && (
          <div className="check">
            <dt className="muted">Creator</dt>
            <dd>
              <A href={walletHref(chain, page.creator.address)}>{short(page.creator.address)}</A>
              <span className="muted">
                {" "}
                {page.creator.tokensCreated} made, {page.creator.graduated} graduated
              </span>
            </dd>
          </div>
        )}
        {page.holders?.count != null && (
          <div className="check">
            <dt className="muted">Holders</dt>
            <dd className="num">{page.holders.count.toLocaleString("en-US")}</dd>
          </div>
        )}
        {t.totalSupply && (
          <div className="check">
            <dt className="muted">Supply</dt>
            <dd className="num">{amount(t.totalSupply)}</dd>
          </div>
        )}
        {page.ath && page.price?.usd != null && (
          <div className="check">
            <dt className="muted">All-time high</dt>
            <dd className="num">
              {price(page.ath.usd)}{" "}
              <span className="muted">
                {age(page.ath.at, now)} ago{page.ath.usd > page.price.usd ? `, ${pct(page.price.usd / page.ath.usd - 1)}` : ""}
              </span>
            </dd>
          </div>
        )}
        <div className="check">
          <dt className="muted">Pools</dt>
          <dd className="num">{page.poolCount}</dd>
        </div>
        {page.chartPool && (
          <div className="check">
            <dt className="muted">Chart pool</dt>
            <dd>
              <Out href={explorerAccount(chain, page.chartPool)}>{short(page.chartPool)}</Out>
            </dd>
          </div>
        )}
      </dl>
      {t.description && <p className="description muted">{t.description}</p>}
    </section>
  );
}

// ─── Tabs under the chart ───

const TABS = ["Trades", "Holders", "Top traders", "Pools", "Dev deploys"] as const;
const holdersPath = (chain: ChainId, token: string) => `/tokens/${chain}/${token}/holders?limit=50`;

function Tabs({ page, chain, address, supply }: { page: TokenPage; chain: ChainId; address: string; supply: number | null }) {
  const [tab, setTab] = useState<(typeof TABS)[number]>("Trades");
  const [tradeMcap, setTradeMcap] = useState(false); // kept here: it survives a look at another tab
  // The same read as the Holders tab: the server's cache answers whichever comes second. Null where the list is not complete.
  const { data: read } = useGet<{ count: number | null }>(holdersPath(chain, address));
  const holders = read?.count ?? page.holders?.count;
  return (
    <section className="card tabs-card">
      <div className="tabs" role="tablist">
        {TABS.map((x) => (
          <button
            key={x}
            role="tab"
            aria-selected={tab === x}
            className={tab === x ? "tab active" : "tab"}
            onClick={(e) => {
              setTab(x);
              e.currentTarget.scrollIntoView({ block: "nearest", inline: "nearest" }); // the tabs scroll sideways on a phone
            }}
          >
            {x}
            {x === "Holders" && holders != null && <span className="muted num"> {compact(holders)}</span>}
            {x === "Pools" && <span className="muted num"> {page.poolCount}</span>}
            {x === "Dev deploys" && page.creator && <span className="muted num"> {page.creator.tokensCreated}</span>}
          </button>
        ))}
      </div>
      {tab === "Trades" && <Trades chain={chain} token={address} symbol={page.token.symbol} supply={supply} mcap={tradeMcap} onToggle={() => setTradeMcap((m) => !m)} />}
      {tab === "Holders" && <Holders chain={chain} token={address} />}
      {tab === "Top traders" && <Traders chain={chain} token={address} />}
      {tab === "Pools" && <Pools page={page} chain={chain} />}
      {tab === "Dev deploys" && <DevDeploys chain={chain} token={address} creator={page.creator} />}
    </section>
  );
}

export const tradeKey = (t: Trade) => `${t.tx}:${t.pool}:${t.eventIndex}`;
const newest = (a: Trade, b: Trade) => b.time - a.time || b.block - a.block || b.txIndex - a.txIndex || b.eventIndex - a.eventIndex;

/** A token's or a wallet's trades, newest first: the recent ones, then each batch as it is indexed. */
export function useTrades(chain: ChainId, scope: "token" | "wallet", address: string) {
  const [trades, setTrades] = useState<Trade[] | null>(null);
  const merge = (batch: Trade[]) =>
    setTrades((prev) => {
      const m = new Map((prev ?? []).map((t) => [tradeKey(t), t]));
      for (const t of batch) m.set(tradeKey(t), t);
      return [...m.values()].sort(newest).slice(0, 100);
    });
  // Swaps indexed while the subscription was not live are not sent: read the recent ones.
  const snapshot = () => get<{ trades: Trade[] }>(`/trades?chain=${chain}&${scope}=${address}&limit=50`).then((r) => merge(r.trades), () => setTrades((p) => p ?? []));
  useEffect(() => void snapshot(), [chain, scope, address]);
  useLive({ channel: "trades", chain, [scope]: address }, (m) => {
    if (m.type === "subscribed" || m.type === "resubscribed") snapshot();
    else if (m.type === "trades") merge(m.data as Trade[]);
    else if (m.type === "retract") {
      const gone = new Set((m.data as Trade[]).map((t) => `${t.block}:${tradeKey(t)}`));
      setTrades((prev) => prev && prev.filter((t) => !gone.has(`${t.block}:${tradeKey(t)}`)));
    }
  });
  return trades;
}

function Trades({ chain, token, symbol, supply, mcap, onToggle }: { chain: ChainId; token: string; symbol: string | null; supply: number | null; mcap: boolean; onToggle: () => void }) {
  const trades = useTrades(chain, "token", token);
  const now = useNow(1000);

  if (!trades) return <p className="table-note muted">Loading trades…</p>;
  if (!trades.length) return <p className="table-note muted">No trades since indexing began.</p>;
  return (
    <div className="table trades-table" role="table">
      <div className="tr thead" role="row">
        <span className="th">Age</span>
        <span className="th">
          <span className="hide-sm">Type</span>
        </span>
        <span className="th">USD</span>
        <span className="th trade-tokens ellipsis">{symbol ?? "Amount"}</span>
        <span className="th hide-md">For</span>
        {supply === null ? (
          <span className="th">Price</span>
        ) : (
          <button className="th sortable trade-unit" onClick={onToggle} aria-pressed={mcap} title={mcap ? "Market cap: each trade's price times the circulating supply now. Click for the price" : "Click for the market cap"}>
            {mcap && supply !== null ? "MCap" : "Price"}
            <Icon name="trades" size={12} />
          </button>
        )}
        <span className="th maker">Maker</span>
        <span className="th tx-col" />
      </div>
      {trades.map((t) => {
        // A swap under $0.01 sets no price on the API's candles: at that size its price is rounding noise.
        const usdPrice = (t.amountUsd ?? 0) >= 0.01 ? t.priceUsd : null;
        return (
        <div key={tradeKey(t)} className={`tr row ${t.side}`} role="row">
          <span className="td muted num">{age(t.time, now)}</span>
          <span className={`td side-${t.side}`} title={t.side === "buy" ? "Buy" : "Sell"}>
            <span className="hide-sm">{t.side === "buy" ? "Buy" : "Sell"}</span>
            <span className="show-sm">{t.side === "buy" ? "B" : "S"}</span>
          </span>
          <span className={`td num side-${t.side}`}>{usd(t.amountUsd)}</span>
          <span className="td num trade-tokens">{amount(t.amount)}</span>
          <span className="td num muted hide-md">
            {amount(t.counterAmount)} {t.counter.symbol}
          </span>
          <span className="td num">{mcap && supply !== null ? usd(usdPrice === null ? null : usdPrice * supply) : price(usdPrice)}</span>
          <span className="td maker">
            {t.trader ? <A href={walletHref(chain, t.trader)}>{short(t.trader)}</A> : <span className="muted">–</span>}
            <Labels labels={t.labels} />
          </span>
          <span className="td tx-col">
            <Out href={explorerTx(chain, t.tx)} className="icon-link" title="Open the transaction">
              <Icon name="out" size={14} />
            </Out>
          </span>
        </div>
        );
      })}
    </div>
  );
}

type Holder = { address: string; balance: string; share: number | null; valueUsd: number | null; tags: string[]; labels: string[] };

function Holders({ chain, token }: { chain: ChainId; token: string }) {
  const { data, error } = useGet<{ holders: Holder[]; count: number | null; complete: boolean; coverage: string | null }>(holdersPath(chain, token));
  if (error) return <p className="table-note muted">Holders could not be read right now ({error.message}).</p>;
  if (!data) return <p className="table-note muted">Reading holders from the chain…</p>;
  return (
    <>
      {!data.complete && data.coverage && <p className="table-note muted">{data.coverage}</p>}
      <div className="table holders-table" role="table">
        <div className="tr thead" role="row">
          <span className="th">#</span>
          <span className="th maker">Address</span>
          <span className="th">Share</span>
          <span className="th hide-sm">Amount</span>
          <span className="th">Value</span>
        </div>
        {data.holders.map((h, i) => (
          <div key={h.address} className="tr row" role="row">
            <span className="td muted num">{i + 1}</span>
            <span className="td maker">
              {/* A pool or a burn address is no one's wallet: its account is on the explorer. */}
              {h.tags.includes("pool") || h.tags.includes("burn") ? <Out href={explorerAccount(chain, h.address)}>{short(h.address)}</Out> : <A href={walletHref(chain, h.address)}>{short(h.address)}</A>}
              <Labels labels={[...h.tags, ...h.labels]} />
            </span>
            <span className="td num">
              <span className="share-bar" style={{ width: `${Math.min(1, h.share ?? 0) * 48}px` }} aria-hidden="true" />
              {share(h.share)}
            </span>
            <span className="td num hide-sm">{amount(h.balance)}</span>
            <span className="td num">{usd(h.valueUsd)}</span>
          </div>
        ))}
      </div>
    </>
  );
}

type Trader = {
  wallet: string;
  labels: string[];
  buys: number;
  sells: number;
  boughtUsd: number;
  soldUsd: number;
  realizedPnlUsd: number | null;
  unrealizedPnlUsd: number | null;
  totalPnlUsd: number | null;
};

function Traders({ chain, token }: { chain: ChainId; token: string }) {
  const { data, error } = useGet<{ traders: Trader[]; indexedSince: number }>(`/tokens/${chain}/${token}/traders?sort=pnl&window=all&limit=50`);
  if (error) return <p className="table-note muted">Traders could not be loaded ({error.message}).</p>;
  if (!data) return <p className="table-note muted">Loading traders…</p>;
  if (!data.traders.length) return <p className="table-note muted">No trader since indexing began.</p>;
  const pnl = (v: number | null) => <span className={`num ${v == null || v === 0 ? "" : v > 0 ? "up" : "down"}`}>{v == null ? "–" : `${v > 0 ? "+" : ""}${usd(v)}`}</span>;
  return (
    <>
      <p className="table-note muted">By profit since indexing began {age(data.indexedSince)} ago, at the average cost of each wallet's buys.</p>
      <div className="table traders-table" role="table">
        <div className="tr thead" role="row">
          <span className="th">#</span>
          <span className="th maker">Wallet</span>
          <span className="th hide-sm">Bought</span>
          <span className="th hide-sm">Sold</span>
          <span className="th hide-md">Unrealized</span>
          <span className="th">PnL</span>
          <span className="th hide-sm">Txns</span>
        </div>
        {data.traders.map((t, i) => (
          <div key={t.wallet} className="tr row" role="row">
            <span className="td muted num">{i + 1}</span>
            <span className="td maker">
              <A href={walletHref(chain, t.wallet)}>{short(t.wallet)}</A>
              <Labels labels={t.labels} />
            </span>
            <span className="td num hide-sm">{usd(t.boughtUsd)}</span>
            <span className="td num hide-sm">{usd(t.soldUsd)}</span>
            <span className="td hide-md">{pnl(t.unrealizedPnlUsd)}</span>
            <span className="td">{pnl(t.totalPnlUsd)}</span>
            <span className="td num muted hide-sm">
              {t.buys}/{t.sells}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

// Every token this token's creator launched: GET /tokens with `creator=`, newest first, each with its peak market cap.
function DevDeploys({ chain, token, creator }: { chain: ChainId; token: string; creator: TokenPage["creator"] }) {
  const path = creator ? `/tokens?chains=${chain}&creator=${creator.address}&limit=50` : null;
  const { data, error } = useGet<{ tokens: TokenRow[]; next: string | null }>(path);
  const [more, setMore] = useState<{ rows: TokenRow[]; next: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const now = useNow(30_000);

  if (!creator) return <p className="table-note muted">Aurelia did not see this token's creation, so its dev is not known.</p>;
  const rows = [...(data?.tokens ?? []), ...(more?.rows ?? [])];
  const next = more ? more.next : data?.next ?? null;
  const loadMore = async () => {
    if (!next || !path) return;
    setLoading(true);
    try {
      const page = await get<{ tokens: TokenRow[]; next: string | null }>(`${path}&cursor=${encodeURIComponent(next)}`);
      setMore((m) => ({ rows: [...(m?.rows ?? []), ...page.tokens], next: page.next }));
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <div className="dev-summary">
        <span className="dev-wallet">
          <span className="muted">Dev</span>
          <A href={walletHref(chain, creator.address)}>{short(creator.address)}</A>
          <Labels labels={creator.labels.filter((l) => l !== "creator")} />
        </span>
        <span>
          <span className="muted">Launched</span> <strong className="num">{creator.tokensCreated}</strong>
        </span>
        <span>
          <span className="muted">Graduated</span> <strong className="num">{creator.graduated}</strong>
          {creator.tokensCreated > 0 && <span className="muted num"> ({share(creator.graduated / creator.tokensCreated)})</span>}
        </span>
        <span className="muted dev-note">Launches seen since indexing began</span>
      </div>
      {error && !data && <p className="table-note muted">This dev's tokens could not be loaded ({error.message}).</p>}
      {!data && !error && <p className="table-note muted">Loading this dev's tokens…</p>}
      {data && rows.length > 0 && (
        <div className="table dev-table" role="table">
          <div className="tr thead" role="row">
            <span className="th token-col">Token</span>
            <span className="th">Age</span>
            <span className="th">MCap</span>
            <span className="th">Peak</span>
            <span className="th hide-sm">Volume</span>
            <span className="th hide-md">Liquidity</span>
            <span className="th">Status</span>
          </div>
          {rows.map((t) => (
            <A key={t.token.address} href={tokenHref(t.chain, t.token.address)} className="tr row">
              <span className="td token-col">
                <TokenLogo src={t.token.logo} symbol={t.token.symbol} size={26} />
                <span className="token-names">
                  <strong className="ellipsis">{t.token.symbol ?? short(t.token.address)}</strong>
                  <span className="muted ellipsis">{t.token.name}</span>
                </span>
                {t.token.address === token && <span className="badge hide-sm">This token</span>}
              </span>
              <span className="td num muted">{t.createdAt ? age(t.createdAt, now) : "–"}</span>
              <span className="td num">{usd(t.marketCapUsd)}</span>
              <span className="td num">{usd(t.peakMarketCapUsd)}</span>
              <span className="td num hide-sm">{usd(t.volume24hUsd)}</span>
              <span className="td num hide-md">{usd(t.liquidityUsd)}</span>
              <span className="td dev-status">
                {t.launchpad?.complete ? (
                  <span className="up" title="Graduated">
                    <span className="hide-sm">Graduated</span>
                    <span className="show-sm">Grad.</span>
                  </span>
                ) :  t.launchpad?.progress != null ? <span className="dev-curve num">{share(t.launchpad.progress)}</span> : (
                  <span className="muted">–</span>
                )}
              </span>
            </A>
          ))}
          {next && (
            <button className="pill ghost more" onClick={loadMore} disabled={loading}>
              {loading ? "Loading…" : "Show more"}
            </button>
          )}
        </div>
      )}
    </>
  );
}

const PROTOCOLS: Record<string, string> = { pumpfun: "pump.fun curve", pumpswap: "PumpSwap", fourmeme: "four.meme curve" };
const protocolName = (p: string) => PROTOCOLS[p] ?? LAUNCHPADS[p] ?? p.replace(/-/g, " ");

function Pools({ page, chain }: { page: TokenPage; chain: ChainId }) {
  return (
    <div className="table pools-table" role="table">
      <div className="tr thead" role="row">
        <span className="th maker">Pool</span>
        <span className="th hide-sm">Price</span>
        <span className="th">Liquidity</span>
        <span className="th">Volume 24h</span>
        <span className="th hide-sm">Txns 24h</span>
      </div>
      {page.pools.map((p) => (
        <div key={p.address} className="tr row" role="row">
          <span className="td maker">
            <Out href={explorerAccount(chain, p.address)}>
              {page.token.symbol ?? "?"} / {p.counter.symbol ?? short(p.counter.address)}
            </Out>
            <span className="muted pool-protocol">
              {LAUNCHPADS[p.protocol] ? <Launchpad id={p.protocol} /> : protocolName(p.protocol)}
            </span>
            {p.address === page.chartPool && <span className="badge">Chart</span>}
          </span>
          <span className="td num hide-sm">{price(p.priceUsd)}</span>
          <span className="td num">{usd(p.liquidityUsd)}</span>
          <span className="td num">{usd(p.volume24hUsd)}</span>
          <span className="td num hide-sm">{compact(p.trades24h)}</span>
        </div>
      ))}
      {page.poolCount > page.pools.length && <p className="table-note muted">The {page.pools.length} busiest of {page.poolCount} pools.</p>}
    </div>
  );
}

